/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6.5 — the `localStorage` contract of the header "Independent Rating"
 * popup (`#component_selector_button`).
 *
 * ★★ BYTE-COMPATIBILITY WITH LEGACY. When this was written (Phase 6.5),
 * `/sizer/`, `/admin/` and `/partners/` still ran the LEGACY Backbone popup
 * (header/view/header.js + component_selector/view/component_selector.popup.js)
 * sharing these exact keys, including a cross-tab `window` `storage` listener
 * (header.js:238-246 → js_scripts/local_storage.js:33-38). Those pages have
 * since moved to React and all use this popup via `GlobalHeader`, so no legacy
 * reader remains — but every value written here is still byte-identical to
 * what `fn.local_storage.set` wrote (plain strings for scalars,
 * `JSON.stringify` for objects — local_storage.js:9-14), so values already in
 * users' browsers keep parsing.
 *
 * THE FOUR CONTRACTS
 *
 * 1. `independent_rating_popup_state` ∈ `'open' | 'minimize' | 'close'`
 *    - `'open'`     ← component_selector.js:111 (renderPopupContent — i.e. ONLY
 *                     on the popup's FIRST construction).
 *    - `'minimize'` ← component_selector.js:287 (minimize).
 *    - `'close'`    ← component_selector.popup.js:56 (the CLOSE button).
 *                     (chassis `cancel` :269 also writes it, but `#cancel` is
 *                     never rendered in header mode — dead.)
 *    - read on page load (header.js:232-236 → changeState) → `'open'`
 *      AUTO-REOPENS the popup; read again for the floating icon
 *      (header.js:566-569 → visible iff `'minimize'`).
 *
 * 2. `active_independent_rating` — the remembered tab. Written on EVERY tab
 *    render (component_selector.js:129, with `'all' → 'fan'` at :128); read once
 *    at popup construction with `|| 'fan'` (component_selector.popup.js:42).
 *
 * 3./4. Four per-selector saved-input maps, each keyed by MANUFACTURER id, with
 *    exactly one entry carrying `last:true`:
 *      fan_independent_ratings                  (fan.selector.js:2380-2397)
 *      coil_independent_ratings                 (coil.selector.js:1479-1490)
 *      rotary_independent_ratings               (energy_wheel.selector.js:1386-1402)
 *      plate_heat_exchanger_independent_ratings (plate_heat_exchanger.selector.js:1467-1480)
 *    On construction the header branch loads the `last:true` entry as the seed
 *    (fan.selector.js:704-737) and, when that entry carries `rate:true`,
 *    AUTO-RE-FIRES the rate to restore the results table (fan :1241 →
 *    `self.rate(null, needToRateData.choosen_index)`). Switching manufacturer
 *    restores that manufacturer's own entry (`findFromStoraga`, fan :4903).
 */

/** `independent_rating_popup_state` — the exact legacy value vocabulary. */
export type IndependentRatingPopupState = 'open' | 'minimize' | 'close';

export const POPUP_STATE_KEY = 'independent_rating_popup_state';
export const ACTIVE_TAB_KEY = 'active_independent_rating';

/**
 * tab name → its saved-inputs key. NOTE the energy-wheel key is
 * `rotary_independent_ratings`, NOT `energy_wheel_…` (energy_wheel.selector.js
 * :1386) — the legacy `rotary` alias. Absent on purpose: gas_heater / uvc /
 * filter (their `li` is `hide`, so no UI path reaches them and they never write).
 */
export const RATING_STORAGE_KEYS: Record<string, string> = {
  fan: 'fan_independent_ratings',
  coil: 'coil_independent_ratings',
  energy_wheel: 'rotary_independent_ratings',
  plate_heat_exchanger: 'plate_heat_exchanger_independent_ratings',
};

/**
 * One saved entry. Shape observed LIVE after a fan rate (discovery §4.3):
 *   {"64": {"state":{…}, "rate":true, "last":true, "model_data":{…},
 *           "choosen_index":0, "motor_choosen_index":…, "fan_mfg_name":"…"}}
 *
 * ★ `model_data` is written by legacy (`saveInStorage`) but the READ path is
 * tolerant of its absence — `{...self.defaults, ...model.model_data}` with an
 * undefined spread is just `defaults` (fan.selector.js:713-716). `state` is the
 * load-bearing member: it is the one legacy pushes into the Backbone state model
 * (`self.state.set(model.state)`, :720) and the one `findFromStoraga` mutates
 * (`delete model.state.motor_power`, :2354). The React writer therefore ALWAYS
 * emits `state` and emits a `model_data` shaped `{model, params_fan,
 * params_motor}` for the legacy readers.
 *
 * ★ `mfg_id` is deliberately NOT written: `saveInStorage` never writes it, so
 * the legacy reader's `self.mfg_id = model.mfg_id` (:726) lands `undefined`.
 * That is legacy behaviour and the observed live shape has no `mfg_id` key —
 * mirrored verbatim rather than "fixed".
 */
export interface IndependentRatingEntry {
  /** the Backbone state model's toJSON. Present for fan / energy_wheel / phx;
   * the COIL writer has it COMMENTED OUT (coil.selector.js:1482) and writes an
   * explicit `mfg_id` instead — so it is optional. */
  state?: Record<string, unknown>;
  rate: boolean;
  last: boolean;
  model_data?: Record<string, unknown>;
  choosen_index?: number | null;
  motor_choosen_index?: number | null;
  fan_mfg_name?: string | null;
  [key: string]: unknown;
}

export type IndependentRatingMap = Record<string, IndependentRatingEntry>;

// ---------------------------------------------------------------------------
// scalars
// ---------------------------------------------------------------------------

/** `fn.local_storage.get` for a scalar key (local_storage.js:15-19 — a value
 * without a `{` is returned raw). */
export function getPopupState(): IndependentRatingPopupState | null {
  const raw = safeGet(POPUP_STATE_KEY);
  return raw === 'open' || raw === 'minimize' || raw === 'close' ? raw : null;
}

/** `fn.local_storage.set(key, 'open'|'minimize'|'close')` — a PLAIN string, no
 * JSON quoting (local_storage.js:9-14). */
export function setPopupState(state: IndependentRatingPopupState): void {
  safeSet(POPUP_STATE_KEY, state);
}

/** component_selector.popup.js:42 — `localStorage.getItem(...) || 'fan'`. */
export function getActiveTab(): string {
  return safeGet(ACTIVE_TAB_KEY) || 'fan';
}

/** component_selector.js:128-129 — written on EVERY tab render, with the
 * `'all' → 'fan'` fold. */
export function setActiveTab(name: string): void {
  safeSet(ACTIVE_TAB_KEY, name === 'all' ? 'fan' : name);
}

// ---------------------------------------------------------------------------
// the per-selector saved-inputs maps
// ---------------------------------------------------------------------------

/** Read a saved-inputs map. A malformed/absent value yields `{}` — the legacy
 * `saved_data || {}` fold (fan.selector.js:732). */
export function readRatingMap(storageKey: string): IndependentRatingMap {
  const raw = safeGet(storageKey);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as IndependentRatingMap)
      : {};
  } catch {
    return {};
  }
}

/**
 * The `last:true` seed entry (fan.selector.js:705-706
 * `Object.values(saved_data).find(el => el.last)`).
 *
 * DEVIATION (deliberate, documented): legacy then dereferences
 * `model.model_data` unguarded (:713) — a map with no `last:true` entry throws a
 * TypeError and the whole selector fails to construct. That state is
 * unreachable through the legacy writer (`saveInStorage` always stamps
 * `last:true` on the entry it writes, :2385) so it is a latent crash on
 * hand-edited storage, not observable behaviour. We return `null` instead of
 * reproducing an uncaught throw.
 */
export function findLastEntry(map: IndependentRatingMap): IndependentRatingEntry | null {
  for (const entry of Object.values(map)) {
    if (entry && entry.last) return entry;
  }
  return null;
}

/** `findFromStoraga(id)` (fan.selector.js:2339-2347) — that manufacturer's entry. */
export function findEntryByMfg(
  map: IndependentRatingMap,
  mfgId: unknown
): IndependentRatingEntry | null {
  if (mfgId == null || mfgId === '') return null;
  return map[String(mfgId)] ?? null;
}

/**
 * `saveInStorage` byte-for-byte:
 *   1. clear `last` on EVERY existing entry;
 *   2. write the current manufacturer's entry (which carries `last:true`);
 *   3. `localStorage.setItem(key, JSON.stringify(map))`.
 * Returns the map it persisted (the legacy `self.storage_saved_data` mirror).
 *
 * ★ The caller supplies the COMPLETE entry INCLUDING `last: true`, with its
 * members in the SAME INSERTION ORDER the legacy writer emits (JSON.stringify
 * preserves insertion order, and the member set + order differ per selector):
 *   fan    js:2385-2393  state, rate, last, model_data, choosen_index,
 *                        motor_choosen_index, fan_mfg_name
 *   coil   js:1481-1487  rate, last, model_data, rated_coil_index, mfg_id
 *                        (NO `state` — it is commented out at js:1482)
 *   ewheel js:1389-1399  state, rate, last, current_model, dataForRate,
 *                        dataForSave, rated_erwheel_index, mfg_id, generic_mfg,
 *                        er_wheel_mfg
 *   phx    js:1469-1478  state, model_data, rate, last, current_model,
 *                        rated_phx_index, generic_mfg, dataForRate, dataForSave
 * This helper deliberately does NOT re-shape or re-order the entry.
 *
 * ★★ `clearLast` (Phase 6.5 builder 2): THREE of the four legacy writers open
 * with `Object.values(self.storage_saved_data).forEach((el) => el.last = false)`
 * — fan js:2382-2384, coil js:1480, ewheel js:1388. **PHX DOES NOT**
 * (plate_heat_exchanger.selector.js:1465-1479 has no such line), so a user who
 * rates two PHX manufacturers leaves TWO entries carrying `last:true`, and the
 * seed reader `Object.values(saved).find((el) => el.last)` then picks whichever
 * comes FIRST in insertion order — NOT the most recent. That is legacy
 * behaviour on `/sizer/` today; pass `clearLast:false` for PHX to reproduce it
 * rather than "fixing" it (PARITY-ONLY).
 */
export function writeRatingEntry(
  storageKey: string,
  mfgId: unknown,
  entry: IndependentRatingEntry,
  existing?: IndependentRatingMap,
  clearLast = true
): IndependentRatingMap {
  const map = existing ?? readRatingMap(storageKey);
  if (clearLast) {
    for (const e of Object.values(map)) {
      if (e) e.last = false;
    }
  }
  // legacy key: `self.mfg_id || self.state.get('mfg_id')` — the manufacturer id.
  map[String(mfgId)] = entry;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    // quota / private-mode — legacy would throw here; swallow (no UI depends
    // on the write succeeding).
  }
  return map;
}

// ---------------------------------------------------------------------------

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
